/**
 * 🔒 Premium Sync Guard
 * 
 * Synchrone Prüfung ob Cloud-Sync erlaubt ist.
 * Liest den Premium/Trial-Status aus localStorage (geschrieben von PremiumContext).
 * 
 * Wird verwendet in:
 * - packageSyncHelper.js (savePaket, syncPackages)
 * - AddPackageWithAI.js (syncDataWithSubcollections)
 * - Überall wo Cloud-Daten geschrieben/gelesen werden
 */

const STORAGE_KEY = 'premiumSyncStatus';

// Backdoor-E-Mails (gleich wie in PremiumContext)
const PREMIUM_BACKDOOR_EMAILS = [
  'wiesn2022@gmail.com',
  'philipp.rank1987@googlemail.com',
];

/**
 * Prüft ob Cloud-Sync erlaubt ist (Premium oder Trial aktiv)
 * @returns {boolean}
 */
export function isCloudSyncAllowed() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    
    const status = JSON.parse(raw);
    
    // Backdoor: immer erlaubt
    if (status.reason === 'backdoor') return true;
    
    // Prüfe ob allowed UND noch nicht abgelaufen
    if (status.allowed && status.until) {
      const untilDate = new Date(status.until);
      if (untilDate > new Date()) {
        return true;
      }
      // Abgelaufen → nicht erlaubt
      return false;
    }
    
    // Premium ohne Ablaufdatum (unbegrenzt)
    if (status.allowed && !status.until) {
      return true;
    }
    
    return false;
  } catch (e) {
    console.warn('🔒 Fehler beim Lesen des Premium-Sync-Status:', e);
    return false;
  }
}

/**
 * Gibt Details zum aktuellen Sync-Status zurück
 * @returns {{ allowed: boolean, reason: string, until: string|null, daysLeft: number }}
 */
export function getCloudSyncStatus() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { allowed: false, reason: 'none', until: null, daysLeft: 0 };
    
    const status = JSON.parse(raw);
    
    if (status.allowed && status.until) {
      const untilDate = new Date(status.until);
      const now = new Date();
      if (untilDate > now) {
        const daysLeft = Math.ceil((untilDate - now) / (1000 * 60 * 60 * 24));
        return { allowed: true, reason: status.reason, until: status.until, daysLeft };
      }
      return { allowed: false, reason: 'expired', until: status.until, daysLeft: 0 };
    }
    
    return status;
  } catch (e) {
    return { allowed: false, reason: 'error', until: null, daysLeft: 0 };
  }
}

/**
 * Setzt den Premium-Sync-Status (wird von PremiumContext aufgerufen)
 * @param {boolean} allowed - Ob Cloud-Sync erlaubt ist
 * @param {string} reason - 'trial', 'premium', 'backdoor', 'expired', 'none'
 * @param {string|null} until - ISO-Datum bis wann erlaubt
 */
export function setCloudSyncStatus(allowed, reason, until = null) {
  try {
    const status = { allowed, reason, until, updatedAt: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(status));
    console.log(`🔒 Cloud-Sync Status: ${allowed ? '✅ erlaubt' : '❌ gesperrt'} (${reason})`);
    if (allowed && typeof window !== 'undefined') {
      try { window.dispatchEvent(new CustomEvent('kiosk:cloud-sync-allowed', { detail: { reason } })); } catch { /* */ }
    }
  } catch (e) {
    console.warn('🔒 Fehler beim Setzen des Premium-Sync-Status:', e);
  }
}

/**
 * Prüft ob eine E-Mail eine Backdoor-E-Mail ist
 * @param {string} email
 * @returns {boolean}
 */
export function isBackdoorEmail(email) {
  return email && PREMIUM_BACKDOOR_EMAILS.includes(email.toLowerCase());
}

/**
 * Prüft ob ein Paket bereits auf dem Server (in der Cloud) existiert.
 * Bestehende Online-Pakete dürfen auch nach Ablauf der Testphase / ohne Premium
 * weiterhin auf dem Server ausgegeben, zurückgeschickt, bearbeitet oder gelöscht werden;
 * nur NEUE Pakete bleiben im Lokal-Modus rein lokal auf dem Gerät.
 * @param {object} paket
 * @returns {boolean}
 */
export function istOnlinePaket(paket) {
  return Boolean(paket && (paket.inCloud === true || paket.serverId != null || paket.uploaded === true));
}

/**
 * Löscht den Sync-Status (bei Logout)
 */
export function clearCloudSyncStatus() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log('🔒 Cloud-Sync Status gelöscht (Logout)');
  } catch (e) {
    // ignore
  }
}
